'use client'

import { isClient } from '@/utils/client'
import { useViewportSize } from '@mantine/hooks'
import { useParams } from 'next/navigation'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

type UseSidebarAutoScrollProps = {
  footerHeight: number
  headerHeight: number
}

type ScrollCalculations = {
  scrollY: number
  viewportHeight: number
  fullHeight: number
  scrollBottom: number
}

export const useSidebarAutoScroll = ({ footerHeight, headerHeight }: UseSidebarAutoScrollProps) => {
  const { width } = useViewportSize()
  const isMobile = width < 768
  const isTablet = width >= 768 && width < 1024
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { roomId } = useParams()

  // Initialize with a reasonable default height to prevent layout shifts
  const [sidebarHeight, setSidebarHeight] = useState(() => {
    if (isClient()) return window.innerHeight - headerHeight
    return 0
  })

  // Get current scroll calculations
  const getScrollCalculations = useCallback((): ScrollCalculations => {
    if (!isClient()) {
      return { scrollY: 0, viewportHeight: 0, fullHeight: 0, scrollBottom: 0 }
    }
    const scrollY = window.scrollY
    const viewportHeight = window.innerHeight
    const fullHeight = document.documentElement.scrollHeight
    const scrollBottom = fullHeight - (scrollY + viewportHeight)
    return { scrollY, viewportHeight, fullHeight, scrollBottom }
  }, [])

  // Calculate sidebar height based on current scroll position
  const calculateSidebarHeight = useCallback(() => {
    if (!isClient()) return 0
    if (isMobile) return window.innerHeight
    if (isTablet) return window.innerHeight - headerHeight

    const { scrollBottom } = getScrollCalculations()
    const overlap = scrollBottom <= footerHeight ? footerHeight - scrollBottom : 0
    return window.innerHeight - headerHeight - overlap
  }, [footerHeight, headerHeight, isMobile, isTablet, getScrollCalculations])

  // Update sidebar height only if changed
  const updateSidebarHeight = useCallback(() => {
    const newHeight = calculateSidebarHeight()
    setSidebarHeight((prevHeight) => (Math.abs(prevHeight - newHeight) > 1 ? newHeight : prevHeight))
  }, [calculateSidebarHeight])

  // Check if scroll position is near bottom
  const isNearBottom = useCallback(() => {
    const { fullHeight, scrollY, viewportHeight } = getScrollCalculations()
    return fullHeight - (scrollY + viewportHeight) <= footerHeight / 2
  }, [footerHeight, getScrollCalculations])

  // Scroll to appropriate position with debounce
  const scrollToPosition = useCallback(() => {
    if (isMobile || isTablet || roomId) return

    // Clear previous timeout to prevent multiple scrolls
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      const { scrollBottom } = getScrollCalculations()

      if (isNearBottom()) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        })
      } else if (scrollBottom <= footerHeight) {
        window.scrollTo({
          top: document.documentElement.scrollHeight - footerHeight - window.innerHeight,
          behavior: 'smooth',
        })
      }
    }, 200)
  }, [footerHeight, isMobile, isTablet, getScrollCalculations, isNearBottom, roomId])

  // Effect: Update sidebar height on resize, scroll, and DOM changes
  useLayoutEffect(() => {
    if (!isClient() || isMobile) return

    updateSidebarHeight()

    window.addEventListener('scroll', updateSidebarHeight, { passive: true })
    window.addEventListener('resize', updateSidebarHeight, { passive: true })

    const resizeObserver = new ResizeObserver(updateSidebarHeight)
    if (document.documentElement) {
      resizeObserver.observe(document.documentElement)
    }

    return () => {
      window.removeEventListener('scroll', updateSidebarHeight)
      window.removeEventListener('resize', updateSidebarHeight)
      resizeObserver.disconnect()
    }
  }, [updateSidebarHeight, isMobile])

  return { sidebarHeight }
}
